import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Drawer,
  Row,
  Select,
} from 'antd';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';

import EmailSenderSelector from './selectors/EmailSenderSelector';
import EmailBody from './EmailBody';
import EmailSubject from './EmailSubject';

import DrawerSubmitFooter from '../../common/containers/DrawerSubmitFooter';
import EmailRecipientSelectors from './selectors/EmailRecipientSelectors';
import useFormEmailOptions from '../../common/hooks/useFormEmailOptions';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import RelativeTimeSelector from '../../subtasks/RelativeTimeSelctor';
import ScheduleDateRepeatSelector from '../../schedule/ScheduleDateRepeatSelector';

export default function WorkflowEmailConfigureDrawer({
  id,
  visible,
  onClose,
  onSubmit,
  data,
  setDataMap,
  divisionId,
  divisionIds = [],
  sections = [],
  isExternalForm,
  setDisableNext,
  setShowErrorPopover,
}) {
  const {
    additionalOptions,
    subjectOptions,
    bodyOptions,
    attachmentFieldOptions,
    dateTimeFields,
    emailFormFields,
  } = useFormEmailOptions(sections);

  const localTimezone = DateTime.local().zoneName;

  const [groupRecipients, setGroupRecipients] = useState(data?.groupRecipients || false);
  const [dontSendFormPdf, setDontSendFormPdf] = useState(data?.dontSendFormPdf || false);
  const [scheduled, setScheduled] = useState(data?.scheduled || false);
  const [scheduledRepeat, setScheduledRepeat] = useState(data?.scheduledRepeat || null);
  const [stopWhenWorkflowComplete, setStopWhenWorkflowComplete] = useState(
    data?.stopWhenWorkflowComplete || false,
  );

  const [anchorOffset, setAnchorOffset] = useState(data?.scheduledAnchorOffset || {});
  const [endOffset, setEndOffset] = useState(data?.scheduledEndOffset || {});
  const [useAnchorDateField, setUseAnchorDateField] = useState(!!data?.scheduledAnchorFieldId);
  const [anchorDateField, setAnchorDateField] = useState(data?.scheduledAnchorFieldId || null);

  useEffect(() => {
    if (!data) return;
    setGroupRecipients(data.groupRecipients);
    setDontSendFormPdf(data.dontSendFormPdf || false);
    setScheduled(data.scheduled || false);
    setScheduledRepeat(data.scheduledRepeat || null);
    setStopWhenWorkflowComplete(data.stopWhenWorkflowComplete || false);
    setAnchorOffset(data.scheduledAnchorOffset || {});
    setEndOffset(data.scheduledEndOffset || {});
    setUseAnchorDateField(!!data.scheduledAnchorFieldId);
    setAnchorDateField(data.scheduledAnchorFieldId || null);
  }, [data]);

  const updateData = useCallback((updates) => {
    setDataMap((dataMap) => {
      const { [id]: oldData = {} } = dataMap;
      return {
        ...dataMap,
        [id]: { ...oldData, ...updates },
      };
    });
  }, [id, setDataMap]);

  const onAttachmentFieldChanged = useCallback((newFields) => {
    updateData({ attachmentFields: newFields });
  }, [updateData]);

  const onCheckChange = useCallback((key) => (checked) => {
    if (key === 'groupRecipients') setGroupRecipients(checked);
    if (key === 'dontSendFormPdf') setDontSendFormPdf(checked);
    if (key === 'stopWhenWorkflowComplete') setStopWhenWorkflowComplete(checked);
    updateData({ [key]: checked });
  }, [updateData]);

  const onScheduledChange = useCallback((checked) => {
    setScheduled(checked);
    const updates = { scheduled: checked };
    if (checked) {
      updates.scheduledTimezone = localTimezone;
    }
    updateData(updates);
  }, [updateData, localTimezone]);

  const onAnchorOffsetChange = useCallback((field) => (value) => {
    const newOffset = { ...anchorOffset, [field]: value };
    setAnchorOffset(newOffset);
    updateData({ scheduledAnchorOffset: newOffset });
  }, [anchorOffset, updateData]);

  const onEndOffsetChange = useCallback((field) => (value) => {
    const newOffset = { ...endOffset, [field]: value };
    setEndOffset(newOffset);
    updateData({ scheduledEndOffset: newOffset });
  }, [endOffset, updateData]);

  const onUseAnchorDateFieldChange = useCallback((checked) => {
    setUseAnchorDateField(checked);
    if (checked) {
      setAnchorOffset({});
      updateData({ scheduledAnchorOffset: null, scheduledAnchorFieldId: null });
      setAnchorDateField(null);
    } else {
      setAnchorDateField(null);
      updateData({ scheduledAnchorFieldId: null });
    }
  }, [updateData]);

  const onAnchorDateFieldChange = useCallback((value) => {
    setAnchorDateField(value || null);
    updateData({ scheduledAnchorFieldId: value || null });
  }, [updateData]);

  const onRepeatChange = useCallback((value) => {
    const repeatValue = value === null ? null : value;
    setScheduledRepeat(repeatValue);
    updateData({ scheduledRepeat: repeatValue });
  }, [updateData]);

  const anchorStartTime = useMemo(() => {
    if (!anchorOffset?.amount || !anchorOffset?.time) return null;
    const base = DateTime.local();
    const dur = anchorOffset.time === 'weeks'
      ? { weeks: anchorOffset.amount }
      : { days: anchorOffset.amount };
    return base.plus(dur).toMillis();
  }, [anchorOffset]);

  return (
    <Drawer
      title="Configure Email Options"
      width={750}
      onClose={onClose}
      visible={visible}
      bodyStyle={{ paddingBottom: 60 }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <EmailSenderSelector
        id={id}
        data={data}
        setDataMap={setDataMap}
      />
      <EmailSubject
        id={id}
        data={data}
        setDataMap={setDataMap}
        fields={subjectOptions}
        visible={visible}
        dateTimeFields={dateTimeFields}
        textFields={emailFormFields}
      />
      <EmailBody
        id={id}
        data={data}
        setDataMap={setDataMap}
        fields={bodyOptions}
        visible={visible}
        dateTimeFields={dateTimeFields}
        textFields={emailFormFields}
      />
      <Row style={{ margin: '10px 0px' }}>
        <OnTraccrCheckbox
          defaultChecked={data?.groupRecipients}
          value={groupRecipients}
          onChange={onCheckChange('groupRecipients')}
          label="Group Recipients Together"
        />
      </Row>
      <EmailRecipientSelectors
        id={id}
        data={data}
        setDataMap={setDataMap}
        divisionId={divisionId}
        divisionIds={divisionIds}
        additionalOptions={additionalOptions}
        sections={sections}
        isExternalForm={isExternalForm}
        setDisableNext={setDisableNext}
        setShowErrorPopover={setShowErrorPopover}
      />
      <Row style={{ marginTop: 5 }}>
        Attach files from field:
      </Row>
      <Row style={{ margin: '10px 0px' }}>
        <Select
          options={attachmentFieldOptions}
          defaultValue={data?.attachmentFields}
          onChange={onAttachmentFieldChanged}
          allowClear
          mode="multiple"
          style={{ width: '100%' }}
        />
      </Row>
      <Row style={{ margin: '10px 0px' }}>
        <OnTraccrCheckbox
          defaultChecked={data?.dontSendFormPdf}
          value={dontSendFormPdf}
          onChange={onCheckChange('dontSendFormPdf')}
          label="Don't Send Form PDF"
        />
      </Row>
      <Row style={{ margin: '10px 0px' }}>
        <OnTraccrCheckbox
          defaultChecked={data?.scheduled}
          value={scheduled}
          onChange={onScheduledChange}
          label="Scheduled"
        />
      </Row>
      {scheduled && (
        <>
          <Row style={{ margin: '10px 0px' }}>
            <OnTraccrCheckbox
              value={useAnchorDateField}
              onChange={onUseAnchorDateFieldChange}
              label="Link start date to a form date/time field"
            />
          </Row>
          {useAnchorDateField ? (
            <>
              <div style={{ marginTop: 10, marginBottom: 5, fontWeight: 500 }}>Start Date Field</div>
              <Select
                options={dateTimeFields.map(({ value, name }) => ({ value, label: name }))}
                value={anchorDateField}
                onChange={onAnchorDateFieldChange}
                allowClear
                style={{ width: '100%' }}
                placeholder="Select a date/time field"
              />
            </>
          ) : (
            <>
              <div style={{ marginTop: 10, marginBottom: 5, fontWeight: 500 }}>Start Sending</div>
              <RelativeTimeSelector
                amount={anchorOffset.amount}
                time={anchorOffset.time}
                onUpdateAmount={onAnchorOffsetChange('amount')}
                onSelectTime={onAnchorOffsetChange('time')}
                text="after step triggers"
              />
            </>
          )}
          <div style={{ marginTop: 10, marginBottom: 5, fontWeight: 500 }}>Repeat</div>
          <ScheduleDateRepeatSelector
            startTime={anchorStartTime}
            value={scheduledRepeat}
            onChange={onRepeatChange}
            allowBiweekly
          />
          {scheduledRepeat && (
            <>
              <div style={{ marginTop: 10, marginBottom: 5, fontWeight: 500 }}>Stop Sending</div>
              <RelativeTimeSelector
                amount={endOffset.amount}
                time={endOffset.time}
                onUpdateAmount={onEndOffsetChange('amount')}
                onSelectTime={onEndOffsetChange('time')}
                text={useAnchorDateField ? 'after start date' : 'after step triggers'}
              />
              <Row style={{ margin: '20px 0px' }}>
                <OnTraccrCheckbox
                  defaultChecked={data?.stopWhenWorkflowComplete}
                  value={stopWhenWorkflowComplete}
                  onChange={onCheckChange('stopWhenWorkflowComplete')}
                  label="Stop When Workflow Complete"
                />
              </Row>
            </>
          )}
        </>
      )}
      <DrawerSubmitFooter
        onClose={onClose}
        onSubmit={onSubmit}
      />
    </Drawer>
  );
}

WorkflowEmailConfigureDrawer.propTypes = {
  id: PropTypes.string,
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  data: PropTypes.shape({
    sender: PropTypes.string,
    subject: PropTypes.string,
    body: PropTypes.string,
    recipients: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
      value: PropTypes.string,
    })),
    attachmentFields: PropTypes.arrayOf(PropTypes.string),
    groupRecipients: PropTypes.bool,
    dontSendFormPdf: PropTypes.bool,
    scheduled: PropTypes.bool,
    scheduledAnchorOffset: PropTypes.shape({
      amount: PropTypes.number,
      time: PropTypes.string,
    }),
    scheduledEndOffset: PropTypes.shape({
      amount: PropTypes.number,
      time: PropTypes.string,
    }),
    scheduledTimezone: PropTypes.string,
    scheduledRepeat: PropTypes.string,
    scheduledAnchorFieldId: PropTypes.string,
    stopWhenWorkflowComplete: PropTypes.bool,
  }),
  setDataMap: PropTypes.func,
  divisionId: PropTypes.string,
  divisionIds: PropTypes.arrayOf(PropTypes.string),
  sections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    fields: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      selectedType: PropTypes.string,
      configProps: PropTypes.shape({
        dataType: PropTypes.string,
        title: PropTypes.string,
      }),
    })),
  })),
  isExternalForm: PropTypes.bool,
  setDisableNext: PropTypes.func,
  setShowErrorPopover: PropTypes.func,
};

WorkflowEmailConfigureDrawer.defaultProps = {
  id: null,
  visible: false,
  onClose: null,
  onSubmit: null,
  data: null,
  setDataMap: null,
  divisionId: null,
  divisionIds: [],
  sections: [],
  isExternalForm: false,
  setDisableNext: null,
  setShowErrorPopover: null,
};
